import React, { useRef, useState } from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Password } from 'primereact/password';

import { authService } from 'services';
import { _Object } from 'utils/interfaces';
import { showTostify } from 'utils/common-functions';
import CustomToast from '../form-inputs/alert';

interface ChangePasswordProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

const ChangePassword = ({ visible, setVisible }: ChangePasswordProps) => {
  const [loading, setLoading] = useState(false);
  const toastRef = useRef<any>(null);
  const formik: _Object = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: ""
    },
    enableReinitialize: true,

    validationSchema: Yup.object({
      current_password: Yup
        .string()
        .required('Current password is required')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/,
          'Password must contain at least one lowercase letter, one uppercase letter, one digit, and one special character'
        ),
      new_password: Yup
        .string()
        .required('Password is required')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/,
          'Password must contain at least one lowercase letter, one uppercase letter, one digit, and one special character'
        ),
      confirm_password: Yup
        .string()
        .oneOf([Yup.ref('new_password')], 'Passwords must match')
        .required('Confirm password is required')
    }),
    onSubmit: async (values: any) => {
      setLoading(true);
      try {
        const result = await authService.changePassword(values);
        if (!result.error) {
          showTostify(toastRef, 'success', result.message);
          formik.resetForm();
          setTimeout(() => {
            setVisible(false);
          }, 1000);
        }
        else {
          showTostify(toastRef, 'error', result.message);
        }
      } catch (error: any) {
        showTostify(toastRef, 'error', error);
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <Dialog header="Change Password" className='change-password' visible={visible} style={{ width: '400px' }} onHide={() => setVisible(false)}>
      <CustomToast ref={toastRef} />
      <form onSubmit={formik.handleSubmit}>
        <div className="form-group">
          <label htmlFor="current_password">Current password<span className='required-field'>*</span></label>
          <Password
            id="current_password"
            className={`form-control ${formik.errors.current_password && formik.touched.current_password ? 'p-invalid' : ''}`}
            name="current_password"
            placeholder="Enter here"
            value={formik.values.current_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            toggleMask
            feedback={false}
          />
          {formik.errors.current_password && formik.touched.current_password && (
            <Message severity="error" text={formik.errors.current_password} />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="new_password">New password<span className='required-field'>*</span></label>
          <Password
            id="new_password"
            className={`form-control ${formik.errors.new_password && formik.touched.new_password ? 'p-invalid' : ''}`}
            name="new_password"
            placeholder="Enter here"
            value={formik.values.new_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            toggleMask
          />
          {formik.errors.new_password && formik.touched.new_password && (
            <Message severity="error" text={formik.errors.new_password} />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="confirm_password">Confirm Password<span className='required-field'>*</span></label>
          <Password
            id="confirm_password"
            className={`form-control ${formik.errors.confirm_password && formik.touched.confirm_password ? 'p-invalid' : ''}`}
            name="confirm_password"
            placeholder="Enter here"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            toggleMask
            feedback={false}
          />
          {formik.errors.confirm_password && formik.touched.confirm_password && (
            <Message severity="error" text={formik.errors.confirm_password} />
          )}
        </div>
        <Button loading={loading} label="Change Password" className='primary w-full mt-auto' />
      </form>
    </Dialog>
  );
};

export default ChangePassword;
